import { HttpErrorResponse } from "@angular/common/http";
import { computed, inject } from "@angular/core";
import { setError, setLoaded, setLoading, withCallState } from "@dcc/shared/state";
import { notificationEvents } from "@dcc/vestas-online/alerts/domain";
import { tapResponse } from "@ngrx/operators";
import { patchState, signalStore, withComputed, withMethods } from "@ngrx/signals";
import { setAllEntities, setEntity, updateEntities, updateEntity, withEntities } from "@ngrx/signals/entities";
import { injectDispatch } from "@ngrx/signals/events";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { catchError, concatMap, distinctUntilChanged, EMPTY, exhaustMap, filter, pipe, switchMap, tap } from "rxjs";
import { SimpleAnnouncement } from "../models/announcement.model";
import { AnnouncementsApiService } from "../services/announcements-api.service";

export const AnnouncementsApiStore = signalStore(
    { providedIn: "root" },
    withCallState(),
    withEntities<SimpleAnnouncement>(),
    withComputed(({ entities }) => ({
        unreadNotifications: computed(() => entities().some((announcement) => !announcement.seen))
    })),
    withMethods((store, announcementsApi = inject(AnnouncementsApiService), notifications = injectDispatch(notificationEvents)) => ({
        markAsRead: rxMethod<number | null>(
            pipe(
                filter((id): id is number => id !== null),
                filter((announcementID) => !!store.entityMap()[announcementID] && !store.entityMap()[announcementID].seen),
                concatMap((announcementID) => {
                    // Save a snapshot of the current state
                    const snapShot = structuredClone(store.entityMap()[announcementID]);

                    patchState(store, updateEntity({ id: announcementID, changes: { seen: true } }));

                    return announcementsApi.markAsRead(announcementID).pipe(
                        catchError(() => {
                            // If the request fails, revert the state to the snapshot
                            patchState(store, setEntity(snapShot));
                            notifications.translatedToastShown({ message: "markSingleAsReadErrorDescription", type: "error" });
                            return EMPTY;
                        })
                    );
                })
            )
        ),
        markAllAsRead: rxMethod<void>(
            pipe(
                exhaustMap(() => {
                    // Save a snapshot of the current state
                    const snapShot = structuredClone(store.entities());

                    // Optimistically mark all announcements as read
                    patchState(store, updateEntities({ predicate: (entity) => !entity.seen, changes: { seen: true } }));

                    return announcementsApi.markAllAsRead(store.entities().map(({ id }) => id)).pipe(
                        catchError(() => {
                            // If the request fails, revert the state to the snapshot
                            patchState(store, setAllEntities(snapShot));
                            notifications.translatedToastShown({ message: "allMarkedAsReadErrorDescription", type: "error" });
                            return EMPTY;
                        })
                    );
                })
            )
        ),
        load: rxMethod<string | null>(
            pipe(
                distinctUntilChanged(),
                tap(() => patchState(store, setLoading())),
                switchMap(() =>
                    announcementsApi.getAll().pipe(
                        tapResponse({
                            next: (announcements) => patchState(store, setAllEntities(sortAnnouncements(announcements)), setLoaded()),
                            error: (error: HttpErrorResponse) => patchState(store, setError(error.message))
                        })
                    )
                )
            )
        )
    }))
);

export function sortAnnouncements(announcements: SimpleAnnouncement[]): SimpleAnnouncement[] {
    return announcements.toSorted((a, b) => {
        if (a.seen !== b.seen) {
            // If a.seen is false (unseen), it should come before b.seen (true, seen).
            // So, if a.seen is false, return -1. If a.seen is true, return 1.
            return a.seen ? 1 : -1; // Unseen (false) comes first
        }
        // If seen status is the same, sort by published date (newest first).
        return new Date(b.published).getTime() - new Date(a.published).getTime();
    });
}
